import { Box, Stack } from '@mui/material'
import { AnimatePresence, motion } from 'framer-motion'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { useEffect, useRef, useState, type MouseEvent } from 'react'
import {
  DEFAULT_TRANSITION_MS,
  HERO_CAROUSEL_CONFIG,
  slideConfig,
} from './hero/slideConfig'
import PlaceholderSlide from './hero/PlaceholderSlide'
import BookshelfSlide from './hero/slides/BookshelfSlide'
import UnjudgeSlide from './hero/slides/UnjudgeSlide'
import { SlideDefinition } from './hero/types'

type HeroCarouselProps = {
  locale?: string
}

const renderSlide = (
  slide: SlideDefinition,
  isActive: boolean,
  locale?: string,
  isBookshelfExpanded?: boolean,
  onBookshelfExpandedChange?: (expanded: boolean) => void,
) => {
  if (slide.id === 'bookshelf') {
    return (
      <BookshelfSlide
        isActive={isActive}
        slide={slide}
        locale={locale}
        isExpanded={Boolean(isBookshelfExpanded)}
        onExpandedChange={onBookshelfExpandedChange}
      />
    )
  }

  if (slide.id === 'unjudge') {
    return <UnjudgeSlide isActive={isActive} slide={slide} />
  }

  return <PlaceholderSlide slide={slide} />
}

const navigationButtonSx = {
  position: 'absolute',
  top: { xs: 'auto', lg: '50%' },
  bottom: { xs: '14px', lg: 'auto' },
  transform: { xs: 'none', lg: 'translateY(-50%)' },
  width: { xs: 22, lg: 38 },
  height: { xs: 22, lg: 38 },
  zIndex: 3,
  borderRadius: '50%',
  border: '1px solid rgba(255,255,255,0.55)',
  backgroundColor: 'rgba(0,0,0,0.28)',
  color: '#fff',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  p: 0,
  transition: 'all 180ms ease',
  '&:hover': {
    backgroundColor: 'rgba(0,0,0,0.4)',
  },
} as const

const HeroCarousel = ({ locale }: HeroCarouselProps) => {
  const [activeIndex, setActiveIndex] = useState(0)
  const [isPaused, setIsPaused] = useState(false)
  const [slideElapsedMs, setSlideElapsedMs] = useState(0)
  const [isBookshelfExpanded, setIsBookshelfExpanded] = useState(false)
  const normalizedLocale = locale === 'da' ? 'da' : 'en'
  const slides = slideConfig
  const activeSlide = slides[activeIndex]
  const isAutoSlideEnabled = !HERO_CAROUSEL_CONFIG.disableAutoSlideChange
  const hasAdvancedRef = useRef(false)
  const rafIdRef = useRef<number | null>(null)
  const previousTimestampRef = useRef<number | null>(null)

  const progressRatio = Math.min(
    1,
    Math.max(0, slideElapsedMs / activeSlide.durationMs),
  )

  const progressRadius = 8
  const progressCircumference = 2 * Math.PI * progressRadius
  const progressOffset = progressCircumference * (1 - progressRatio)

  const goToSlide = (index: number) => {
    if (index === activeIndex) {
      return
    }

    hasAdvancedRef.current = false
    previousTimestampRef.current = null
    setSlideElapsedMs(0)
    setActiveIndex(index)
  }

  const goToNextSlide = () => {
    goToSlide((activeIndex + 1) % slides.length)
  }

  const goToPreviousSlide = () => {
    goToSlide((activeIndex - 1 + slides.length) % slides.length)
  }

  useEffect(() => {
    hasAdvancedRef.current = false
    previousTimestampRef.current = null

    if (activeSlide.id !== 'bookshelf') {
      setIsBookshelfExpanded(false)
    }
  }, [activeSlide.id])

  useEffect(() => {
    if (rafIdRef.current !== null) {
      window.cancelAnimationFrame(rafIdRef.current)
      rafIdRef.current = null
    }

    if (isPaused || !isAutoSlideEnabled) {
      previousTimestampRef.current = null
      return
    }

    let cancelled = false

    const update = (timestamp: number) => {
      if (cancelled) {
        return
      }

      if (previousTimestampRef.current === null) {
        previousTimestampRef.current = timestamp
      }

      const delta = timestamp - previousTimestampRef.current
      previousTimestampRef.current = timestamp

      setSlideElapsedMs((previousElapsed) => {
        return Math.min(previousElapsed + delta, activeSlide.durationMs)
      })

      if (!cancelled) {
        rafIdRef.current = window.requestAnimationFrame(update)
      }
    }

    rafIdRef.current = window.requestAnimationFrame(update)

    return () => {
      cancelled = true

      if (rafIdRef.current !== null) {
        window.cancelAnimationFrame(rafIdRef.current)
        rafIdRef.current = null
      }

      previousTimestampRef.current = null
    }
  }, [activeSlide.durationMs, activeSlide.id, isAutoSlideEnabled, isPaused])

  useEffect(() => {
    if (
      !isAutoSlideEnabled ||
      isPaused ||
      slideElapsedMs < activeSlide.durationMs ||
      hasAdvancedRef.current
    ) {
      return
    }

    hasAdvancedRef.current = true
    setSlideElapsedMs(0)
    previousTimestampRef.current = null
    setActiveIndex((prevIndex) => (prevIndex + 1) % slides.length)
  }, [
    activeSlide.durationMs,
    isAutoSlideEnabled,
    isPaused,
    slideElapsedMs,
    slides.length,
  ])

  return (
    <Box
      sx={{
        minHeight:
          activeSlide.id === 'bookshelf' && isBookshelfExpanded
            ? { xs: '320px', md: '480px' }
            : { xs: '320px', md: '480px' },
        height:
          activeSlide.id === 'bookshelf' && isBookshelfExpanded
            ? 'auto'
            : { xs: '320px', md: '480px' },
        width: '100%',
        position: 'relative',
        overflow:
          activeSlide.id === 'bookshelf' && isBookshelfExpanded
            ? 'visible'
            : 'hidden',
        boxShadow: '0 1px 1px rgba(0,0,0,0.15)',
      }}
      onClick={goToNextSlide}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <AnimatePresence>
        <motion.div
          key={activeSlide.id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{
            duration: DEFAULT_TRANSITION_MS / 1000,
            ease: 'easeInOut',
          }}
          style={
            isBookshelfExpanded
              ? { position: 'relative', width: '100%' }
              : { position: 'absolute', inset: 0 }
          }
        >
          {renderSlide(
            activeSlide,
            true,
            normalizedLocale,
            isBookshelfExpanded,
            setIsBookshelfExpanded,
          )}
        </motion.div>
      </AnimatePresence>

      <Stack
        direction="row"
        spacing={1}
        sx={{
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          bottom: '20px',
          zIndex: 2,
        }}
      >
        {slides.map((slide, index) => {
          const isActive = index === activeIndex

          return (
            <Box
              key={slide.id}
              component="button"
              type="button"
              onClick={(event: MouseEvent<HTMLButtonElement>) => {
                event.stopPropagation()
                goToSlide(index)
              }}
              aria-label={`Go to slide ${index + 1}`}
              sx={{
                width: isActive ? 24 : 10,
                height: 10,
                borderRadius: 8,
                backgroundColor: isActive
                  ? 'rgba(255,255,255,0.92)'
                  : 'rgba(255,255,255,0.45)',
                transition: 'all 250ms ease-in-out',
                border: 'none',
                cursor: 'pointer',
                p: 0,
                boxShadow: '0 1px 6px rgba(0,0,0,0.35)',
              }}
            />
          )
        })}
      </Stack>

      <Box
        component="button"
        type="button"
        aria-label="Previous slide"
        onClick={(event: MouseEvent<HTMLButtonElement>) => {
          event.stopPropagation()
          goToPreviousSlide()
        }}
        sx={{
          ...navigationButtonSx,
          left: { xs: 'calc(50% - 58px)', lg: '16px' },
        }}
      >
        <ChevronLeftIcon sx={{ fontSize: { xs: 15, lg: 20 } }} />
      </Box>

      <Box
        component="button"
        type="button"
        aria-label="Next slide"
        onClick={(event: MouseEvent<HTMLButtonElement>) => {
          event.stopPropagation()
          goToNextSlide()
        }}
        sx={{
          ...navigationButtonSx,
          right: { xs: 'calc(50% - 58px)', lg: '16px' },
        }}
      >
        <ChevronRightIcon sx={{ fontSize: { xs: 15, lg: 20 } }} />
      </Box>

      <Box
        sx={{
          position: 'absolute',
          right: '14px',
          bottom: '14px',
          width: 22,
          height: 22,
          zIndex: 3,
          borderRadius: '50%',
          backgroundColor: 'rgba(0,0,0,0.28)',
          backdropFilter: 'blur(1px)',
          display: isAutoSlideEnabled ? 'flex' : 'none',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
          <circle
            cx="10"
            cy="10"
            r={progressRadius}
            fill="transparent"
            stroke="rgba(255,255,255,0.28)"
            strokeWidth="2"
          />
          <circle
            cx="10"
            cy="10"
            r={progressRadius}
            fill="transparent"
            stroke="rgba(255,255,255,0.95)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeDasharray={progressCircumference}
            strokeDashoffset={progressOffset}
            transform="rotate(-90 10 10)"
          />
        </svg>

        {isAutoSlideEnabled && isPaused && (
          <Box
            sx={{
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '2px',
            }}
          >
            <Box
              sx={{
                width: '2px',
                height: '7px',
                borderRadius: '2px',
                backgroundColor: '#fff',
              }}
            />
            <Box
              sx={{
                width: '2px',
                height: '7px',
                borderRadius: '2px',
                backgroundColor: '#fff',
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default HeroCarousel
